/**
 * BatchDetailPage Component
 *
 * Main page for batch detail view with:
 * - Tab navigation (Jobs, Results, Raw Input, Analytics, Edit)
 * - Batch header with name, description, job counts, status
 * - "Parse All Jobs" button (conditionally shown)
 * - "Cancel Parsing" button (conditionally shown)
 * - "Run All Jobs" button for batch execution
 * - "Cancel Execution" button during execution
 * - ExecutionProgressBar during execution
 * - Editable concurrency controls with debounced updates
 * - Real-time updates via SSE
 */

import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBatch, useBatchSSE } from '../hooks/useBatches';
import { useExecutionProgress } from '../hooks/useExecutionProgress';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { batchService } from '../services/api';
import { evaluationService } from '../services/evaluationApi';
import { useResults, useDurationStatistics } from '../hooks/useResults';
import toast from 'react-hot-toast';
import { JobsTab } from '../components/batches/JobsTab';
import { ResultsTab } from '../components/batches/ResultsTab';
import { RawInputTab } from '../components/batches/RawInputTab';
import { AnalyticsTab } from '../components/batches/AnalyticsTab';
import { EditTab } from '../components/batches/EditTab';
import { ExecutionProgressBar } from '../components/batches/ExecutionProgressBar';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

type TabType = 'jobs' | 'results' | 'raw-input' | 'analytics' | 'edit';

// Debounce utility function
function debounce<T extends (...args: any[]) => any>(func: T, delay: number): T {
  let timeoutId: NodeJS.Timeout;
  return ((...args: Parameters<T>) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  }) as T;
}

// Format duration statistic (in seconds) to human-readable format
function formatDurationStat(duration: number | null): string {
  if (duration === null || duration === undefined) {
    return '-';
  }
  if (duration < 60) {
    return `${duration.toFixed(1)}s`;
  }
  const minutes = Math.floor(duration / 60);
  const seconds = Math.floor(duration % 60);
  if (minutes < 60) {
    return `${minutes}m ${seconds}s`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

export const BatchDetailPage: React.FC = () => {
  const { batch_id } = useParams<{ batch_id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<TabType>('jobs');
  const [concurrencyInput, setConcurrencyInput] = useState<number | string>('');
  const [showRunAllConfirm, setShowRunAllConfirm] = useState(false);

  // Fetch batch data
  const { data: batch, isLoading, error, refetch } = useBatch(batch_id);

  // Fetch evaluation metrics
  const { data: evalMetrics } = useQuery({
    queryKey: ['batch-eval-metrics', batch_id],
    queryFn: async () => {
      if (!batch_id) return null;
      try {
        return await evaluationService.getEvaluationMetrics(batch_id);
      } catch (error) {
        console.error('Failed to fetch evaluation metrics:', error);
        return null;
      }
    },
    staleTime: 5000,
    refetchInterval: 5000,
    enabled: !!batch_id,
  });

  // Real-time updates via SSE (parsing)
  const { stats, error: sseError } = useBatchSSE(batch_id);

  // Fetch Results counts for stats panel
  const { data: completeResults } = useResults(batch_id, { status: 'complete', limit: 1 });
  const { data: errorResults } = useResults(batch_id, { status: 'error', limit: 1 });
  const { data: emptyResults } = useResults(batch_id, { status: 'empty', limit: 1 });
  const { data: noneResults } = useResults(batch_id, { status: 'none', limit: 1 });

  // Fetch duration statistics
  const { data: durationStats } = useDurationStatistics(batch_id);

  // Check if execution is in progress based on batch status
  const isExecutionInProgress = batch?.status === 'executing' || batch?.status === 'queued_for_execution';

  // Debug logging
  React.useEffect(() => {
    console.log('[BatchDetailPage] Execution status check:', {
      batchStatus: batch?.status,
      isExecutionInProgress,
      shouldEnableSSE: isExecutionInProgress
    });
  }, [batch?.status, isExecutionInProgress]);

  // Real-time execution progress via SSE (only enable when execution is in progress)
  const { progress, isExecuting } = useExecutionProgress(batch_id, isExecutionInProgress);

  // Parse all jobs mutation
  const parseAllMutation = useMutation({
    mutationFn: () => {
      if (!batch_id) throw new Error('Batch ID is required');
      return batchService.parseAllJobs(batch_id);
    },
    onSuccess: (data) => {
      toast.success(`Parsing started! ${data.jobs_enqueued} jobs enqueued for parsing.`);
      queryClient.invalidateQueries({ queryKey: ['batches', batch_id] });
      queryClient.invalidateQueries({ queryKey: ['batch-jobs', batch_id] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start parsing');
    },
  });

  // Cancel parsing mutation
  const cancelParsingMutation = useMutation({
    mutationFn: () => {
      if (!batch_id) throw new Error('Batch ID is required');
      return batchService.cancelParsing(batch_id);
    },
    onSuccess: (data) => {
      toast.success(`Parsing cancelled! ${data.jobs_removed_from_queue} jobs removed from queue.`);
      queryClient.invalidateQueries({ queryKey: ['batches', batch_id] });
      queryClient.invalidateQueries({ queryKey: ['batch-jobs', batch_id] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to cancel parsing');
    },
  });

  // Start execution mutation
  const startExecutionMutation = useMutation({
    mutationFn: () => {
      if (!batch_id) throw new Error('Batch ID is required');
      return batchService.startExecution(batch_id);
    },
    onSuccess: (data) => {
      toast.success(`Execution started! ${data.jobs_queued} jobs queued for execution.`);
      setShowRunAllConfirm(false);
      queryClient.invalidateQueries({ queryKey: ['batches', batch_id] });
      queryClient.invalidateQueries({ queryKey: ['batch-jobs', batch_id] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to start execution');
      setShowRunAllConfirm(false);
    },
  });

  // Cancel execution mutation
  const cancelExecutionMutation = useMutation({
    mutationFn: () => {
      if (!batch_id) throw new Error('Batch ID is required');
      return batchService.cancelExecution(batch_id);
    },
    onSuccess: () => {
      toast.success('Execution cancelled. In-flight jobs will complete.');
      queryClient.invalidateQueries({ queryKey: ['batches', batch_id] });
      queryClient.invalidateQueries({ queryKey: ['batch-jobs', batch_id] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to cancel execution');
    },
  });

  // Update concurrency mutation
  const updateConcurrencyMutation = useMutation({
    mutationFn: (newConcurrency: number) => {
      if (!batch_id) throw new Error('Batch ID is required');
      return batchService.updateExecutionConcurrency(batch_id, { execution_concurrency: newConcurrency });
    },
    onSuccess: (data) => {
      toast.success(`Concurrency updated to ${data.execution_concurrency}`);
      queryClient.invalidateQueries({ queryKey: ['batches', batch_id] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update concurrency');
    },
  });

  // Debounced concurrency update (300ms delay)
  const debouncedUpdateConcurrency = useCallback(
    debounce((newLimit: number) => {
      if (newLimit >= 1 && newLimit <= 100) {
        updateConcurrencyMutation.mutate(newLimit);
      }
    }, 300),
    [batch_id]
  );

  // Handle concurrency input change
  const handleConcurrencyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setConcurrencyInput(value);
    const numValue = parseInt(value, 10);
    if (!isNaN(numValue) && numValue >= 1 && numValue <= 100) {
      debouncedUpdateConcurrency(numValue);
    }
  };

  // Initialize concurrency input when batch loads
  React.useEffect(() => {
    if (batch && concurrencyInput === '') {
      setConcurrencyInput(batch.execution_concurrency || 10);
    }
  }, [batch, concurrencyInput]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" aria-label="Loading batch"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading batch</h3>
                <p className="mt-1 text-sm text-red-700">{error.message}</p>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => refetch()}
                    className="text-sm font-medium text-red-600 hover:text-red-500"
                  >
                    Try again
                  </button>
                  <button
                    onClick={() => navigate(-1)}
                    className="text-sm font-medium text-red-600 hover:text-red-500"
                  >
                    Go back
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!batch) {
    return null;
  }

  // Use SSE stats if available, otherwise use batch data
  const displayData = stats || batch;
  const hasRawOrFailedJobs = (displayData.raw_jobs || 0) + (displayData.parse_failed_jobs || 0) > 0;
  const isParsingActive = (displayData.parsing_jobs || 0) > 0;
  const hasParsedJobs = (displayData.parsed_jobs || 0) > 0;

  // Calculate evaluation metrics from evalMetrics API
  const passCount = evalMetrics?.pass_count || 0;
  const failCount = evalMetrics?.fail_count || 0;
  const errorCount = evalMetrics?.error_count || 0;
  const evaluatedCount = passCount + failCount + errorCount;
  const evalPassRate = evaluatedCount > 0 ? (passCount / evaluatedCount) * 100 : 0;
  const withGroundtruthCount = evalMetrics?.total_jobs_with_groundtruth || 0;

  const tabs: { id: TabType; label: string }[] = [
    { id: 'jobs', label: 'Jobs' },
    { id: 'results', label: 'Results' },
    { id: 'raw-input', label: 'Raw Input' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'edit', label: 'Configure' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Breadcrumb */}
          <nav className="text-sm mb-4">
            <ol className="flex items-center space-x-2">
              <li>
                <button
                  onClick={() => navigate('/projects')}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Projects
                </button>
              </li>
              <li className="text-gray-400">/</li>
              <li>
                <button
                  onClick={() => navigate(`/projects/${batch.project_id}/detail`)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Project
                </button>
              </li>
              <li className="text-gray-400">/</li>
              <li className="text-gray-900 font-medium">{batch.name}</li>
            </ol>
          </nav>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Go back"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">{batch.name}</h1>
                {batch.description && (
                  <p className="mt-1 text-sm text-gray-500">{batch.description}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3">
              {/* Concurrency Control (show during execution or when parsed jobs exist) */}
              {(isExecuting || hasParsedJobs) && (
                <div className="flex items-center gap-2">
                  <label htmlFor="concurrency" className="text-sm font-medium text-gray-700">
                    Concurrency:
                  </label>
                  <input
                    id="concurrency"
                    type="number"
                    min="1"
                    max="100"
                    value={concurrencyInput}
                    onChange={handleConcurrencyChange}
                    disabled={updateConcurrencyMutation.isPending}
                    className="w-20 px-2 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
                    aria-label="Execution concurrency"
                  />
                </div>
              )}
              {/* Cancel Execution Button */}
              {isExecuting && (
                <button
                  onClick={() => cancelExecutionMutation.mutate()}
                  disabled={cancelExecutionMutation.isPending}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                  aria-label="Cancel execution"
                >
                  {cancelExecutionMutation.isPending ? 'Cancelling...' : 'Cancel Execution'}
                </button>
              )}
              {/* Run All Jobs Button */}
              {!isExecuting && !isParsingActive && (
                <button
                  onClick={() => setShowRunAllConfirm(true)}
                  disabled={startExecutionMutation.isPending}
                  className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50"
                  title="Execute all parsed jobs in this batch"
                  aria-label="Run all jobs"
                >
                  Run All Jobs
                </button>
              )}
              {/* Cancel Parsing Button */}
              {isParsingActive && (
                <button
                  onClick={() => cancelParsingMutation.mutate()}
                  disabled={cancelParsingMutation.isPending}
                  className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                  aria-label="Cancel parsing"
                >
                  Cancel Parsing
                </button>
              )}
              {/* Parse All Jobs Button - Always show to allow reparsing */}
              {!isParsingActive && !isExecuting && (
                <button
                  onClick={() => parseAllMutation.mutate()}
                  disabled={parseAllMutation.isPending}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                  aria-label="Parse all jobs"
                  title={hasRawOrFailedJobs ? "Parse all raw and failed jobs" : "Reparse all jobs in this batch"}
                >
                  {hasRawOrFailedJobs ? 'Parse All Jobs' : 'Reparse All Jobs'}
                </button>
              )}
            </div>
          </div>

          {/* Execution Progress Bar */}
          {isExecuting && progress && (
            <div className="mt-6">
              <ExecutionProgressBar
                batchId={batch.batch_id}
                totalJobs={progress.total_jobs}
                running={progress.running}
                completed={progress.completed}
                failed={progress.failed}
                pending={progress.pending}
              />
            </div>
          )}

          {/* Batch Stats Summary - Grouped Layout */}
          <div className="mt-6 space-y-3">
            {/* Row 1: Job Status & Evaluation */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
              <div className="bg-gray-50 rounded-lg p-2.5">
                <dt className="text-xs font-medium text-gray-500">Total</dt>
                <dd className="text-lg font-semibold text-gray-900">{displayData.total_jobs}</dd>
              </div>
              <div className="bg-yellow-50 rounded-lg p-2.5">
                <dt className="text-xs font-medium text-yellow-700">Run / Queue</dt>
                <dd className="text-lg font-semibold text-yellow-900">
                  {displayData.running_jobs || 0} / {displayData.jobs_queued_count || 0}
                </dd>
              </div>
              <div className="bg-green-50 rounded-lg p-2.5">
                <dt className="text-xs font-medium text-green-700">Done / Err</dt>
                <dd className="text-lg font-semibold text-green-900">
                  {completeResults?.total || 0} / {errorResults?.total || 0}
                </dd>
              </div>
              <div className="bg-indigo-50 rounded-lg p-2.5">
                <dt className="text-xs font-medium text-indigo-700">Ground Truth</dt>
                <dd className="text-lg font-semibold text-indigo-900">{withGroundtruthCount}</dd>
              </div>
              <div className="bg-blue-50 rounded-lg p-2.5">
                <dt className="text-xs font-medium text-blue-700">Evaluated</dt>
                <dd className="text-lg font-semibold text-blue-900">{evaluatedCount}</dd>
              </div>
              <div className="bg-emerald-50 rounded-lg p-2.5">
                <dt className="text-xs font-medium text-emerald-700">Passed</dt>
                <dd className="text-lg font-semibold text-emerald-900">{passCount}</dd>
              </div>
              <div className="bg-purple-50 rounded-lg p-2.5">
                <dt className="text-xs font-medium text-purple-700">Pass Rate</dt>
                <dd className="text-lg font-semibold text-purple-900">
                  {evalPassRate.toFixed(0)}%
                </dd>
              </div>
            </div>

            {/* Row 2: Duration Statistics */}
            {durationStats && durationStats.count > 0 && (
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-slate-600">
                    Duration ({durationStats.count} jobs)
                  </span>
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-slate-600">
                      <span className="text-slate-400">Avg:</span>{' '}
                      <span className="font-semibold text-slate-800">
                        {formatDurationStat(durationStats.avg)}
                      </span>
                    </span>
                    <span className="text-slate-600">
                      <span className="text-slate-400">P50:</span>{' '}
                      <span className="font-semibold text-slate-800">
                        {formatDurationStat(durationStats.p50)}
                      </span>
                    </span>
                    <span className="text-slate-600">
                      <span className="text-slate-400">P75:</span>{' '}
                      <span className="font-semibold text-slate-800">
                        {formatDurationStat(durationStats.p75)}
                      </span>
                    </span>
                    <span className="text-slate-600">
                      <span className="text-slate-400">P99:</span>{' '}
                      <span className="font-semibold text-slate-800">
                        {formatDurationStat(durationStats.p99)}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* SSE Status Indicator */}
          {sseError && (
            <div className="mt-4 bg-amber-50 border border-amber-200 rounded-md p-3">
              <p className="text-xs text-amber-700">Real-time updates: {sseError}</p>
            </div>
          )}
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                aria-current={activeTab === tab.id ? 'page' : undefined}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'jobs' && <JobsTab batchId={batch.batch_id} />}
        {activeTab === 'results' && <ResultsTab batchId={batch.batch_id} />}
        {activeTab === 'raw-input' && <RawInputTab batchId={batch.batch_id} />}
        {activeTab === 'analytics' && <AnalyticsTab batchId={batch.batch_id} />}
        {activeTab === 'edit' && (
          <EditTab
            batchId={batch.batch_id}
            parsingDescription={batch.parsing_description}
            parsingFunction={batch.parsing_function}
            csvHeaders={batch.csv_headers}
          />
        )}
      </main>

      {/* Run All Jobs Confirmation Modal */}
      {showRunAllConfirm && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-lg font-medium text-gray-900">Run All Jobs</h3>
                <p className="mt-2 text-sm text-gray-500">
                  This will execute all {displayData.parsed_jobs || 0} parsed jobs in this batch.
                  Jobs will be queued and executed based on the concurrency limit ({batch?.execution_concurrency || 10} concurrent jobs).
                </p>
                <p className="mt-2 text-sm text-amber-600 font-medium">
                  This action cannot be undone. Are you sure you want to proceed?
                </p>
              </div>
            </div>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setShowRunAllConfirm(false)}
                disabled={startExecutionMutation.isPending}
                className="px-4 py-2 bg-white text-gray-700 text-sm font-medium rounded-md border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => startExecutionMutation.mutate()}
                disabled={startExecutionMutation.isPending}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {startExecutionMutation.isPending && (
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {startExecutionMutation.isPending ? 'Starting...' : 'Yes, Run All Jobs'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
